// Two products — TicoVac® for patients 16 years and over (0.5 mL),
// TicoVac Junior® for patients 1–15 years (0.25 mL). Two schedules —
// conventional (3 doses) and rapid/accelerated (3 doses, closer
// intervals). Both schedules apply to both age groups. Boosters differ
// by age: 1–59 years every 5 years after the first booster at 3 years;
// 60 and over (TicoVac® only) every 3 years. SC route is only for
// exceptional bleeding-disorder cases — IM is standard.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.TBE,
  displayName: "Tick-borne encephalitis",
  serviceType: "administration",
  minimumAgeYears: 1,
  indicationSummary:
    "At high risk of tick-borne encephalitis (TBE) through living in, working in, or travelling to TBE-endemic areas with outdoor activities, occupational exposure, or residence in an endemic region. Does not protect against Lyme disease or other tick-borne infections.",

  brands: [
    {
      brandId: "ticovac-adult",
      brandName: "TicoVac\u00ae (16 years and over)",
      minimumAgeYears: 16,
      route: "IM",
      doseDescription: "0.5 mL per dose",
      administrationNotes:
        "Allow the vaccine to reach room temperature before administration. Shake the pre-filled syringe to obtain an off-white opalescent suspension. Inspect before use. Administer by intramuscular injection into the deltoid muscle. Do not use the gluteal region. Subcutaneous administration is only permitted in exceptional cases where intramuscular injection is contraindicated due to a bleeding disorder \u2014 no primary course data exist for the SC route; document the clinical decision.",
    },
    {
      brandId: "ticovac-junior",
      brandName: "TicoVac Junior\u00ae (1\u201315 years)",
      minimumAgeYears: 1,
      maximumAgeYears: 15,
      route: "IM",
      doseDescription: "0.25 mL per dose",
      administrationNotes:
        "Allow the vaccine to reach room temperature before administration. Shake the pre-filled syringe to obtain an off-white opalescent suspension. Inspect before use. Administer by intramuscular injection into the deltoid muscle. In children aged 12\u201317 months or where deltoid muscle mass is inadequate, administer into the vastus lateralis muscle. Do not use the gluteal region.",
    },
  ],

  schedules: [
    {
      scheduleId: "tbe-conventional",
      label:
        "Conventional 3-dose schedule (dose 1; dose 2 at 1\u20133 months; dose 3 at 5\u201312 months after dose 2)",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 first dose",
        },
        {
          doseNumber: 2,
          label: "1\u20133 months after dose 1",
          intervalFromDose1Days: 30,
        },
        {
          doseNumber: 3,
          label: "5\u201312 months after dose 2",
          intervalFromDose1Days: 210,
        },
      ],
      boosterInfo:
        "First booster: 3 years after dose 3 for patients aged 1\u201359 years; 3 years after dose 3 for patients aged 60 and over (TicoVac\u00ae only). Subsequent boosters: every 5 years for ages 1\u201359; every 3 years for ages 60 and over.",
      completionNotes:
        "After the first two doses, protection is expected for the ongoing tick season. All three doses are required for protection lasting 3 or more years. An interrupted course does not need to be restarted \u2014 a single catch-up dose continues the schedule if 2 or more prior doses have been given.",
    },
    {
      scheduleId: "tbe-rapid",
      label:
        "Rapid (accelerated) 3-dose schedule (dose 1; dose 2 at 14 days; dose 3 at 5\u201312 months after dose 2)",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 first dose",
        },
        {
          doseNumber: 2,
          label: "Day 14 \u2014 second dose",
          intervalFromDose1Days: 14,
        },
        {
          doseNumber: 3,
          label: "5\u201312 months after dose 2",
          intervalFromDose1Days: 210,
        },
      ],
      boosterInfo:
        "First booster: 3 years after dose 3 for patients aged 1\u201359; 3 years after dose 3 for patients aged 60 and over (TicoVac\u00ae only). Subsequent boosters: every 5 years for ages 1\u201359; every 3 years for ages 60 and over.",
      completionNotes:
        "Use where time before travel does not allow the conventional schedule. After the first two doses, protection is expected for the ongoing tick season. The third dose is required for protection lasting 3 or more years.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "tbe-ex-under-12-months",
      question: "Is the patient under 12 months of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "TBE vaccine is not licensed for patients under 12 months of age. Do not proceed.",
      referralMessage: "Refer to the patient's GP or paediatric specialist.",
    },
    {
      checkId: "tbe-ex-hypersensitivity",
      question:
        "Does the patient have known hypersensitivity to any active substance, excipient, or production residual of TicoVac\u00ae or TicoVac Junior\u00ae, including formaldehyde, neomycin, gentamicin, or protamine sulphate? Also consider cross-allergy to other aminoglycosides.",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "TBE vaccine is contraindicated in this patient. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "tbe-ex-anaphylaxis-prior",
      question: "Has the patient had a confirmed anaphylactic reaction following a previous dose of a TBE vaccine?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "A previous anaphylactic reaction to a TBE vaccine is a contraindication. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "tbe-ex-egg-anaphylaxis",
      question: "Has the patient had a confirmed anaphylactic reaction following ingestion of eggs or egg-containing food?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "TBE vaccine is contraindicated in patients with a history of anaphylaxis after egg ingestion. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "tbe-ex-neurological",
      question: "Does the patient have an evolving or unstable neurological condition?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Vaccination should be deferred until the neurological condition is stable.",
      referralMessage: "Refer to the patient's GP or neurologist. Rebook when the condition is stable.",
    },
  ],

  cautions: [
    {
      checkId: "tbe-caut-bleeding",
      question: "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy?",
      cautionMessage:
        "Administer by intramuscular injection with care using a fine-gauge needle with firm pressure applied for at least two minutes afterwards. Subcutaneous administration is only permitted in exceptional cases where IM injection is contraindicated due to a bleeding disorder \u2014 no primary course SC data exist. For patients on stable warfarin with INR within therapeutic range, IM injection may proceed. Document the clinical decision.",
    },
    {
      checkId: "tbe-caut-egg-non-severe",
      question: "Does the patient have a non-severe egg allergy (e.g. urticaria, vomiting after egg ingestion, but no anaphylaxis)?",
      cautionMessage:
        "Non-severe egg allergy is a caution, not a contraindication. Vaccination may proceed under enhanced supervision with anaphylaxis facilities immediately available. The observation period should be extended if any concern exists. Document the risk-benefit discussion.",
    },
    {
      checkId: "tbe-caut-febrile-convulsions",
      question: "Does the patient aged 1\u20132 years have a history of febrile convulsions?",
      cautionMessage:
        "Fever is common in children aged 1\u20132 years after the first dose of TBE vaccine. In a child with a history of febrile convulsions, consider prescribing or advising an antipyretic (e.g. paracetamol) to be given at the time of vaccination and as needed for the following 24 hours. Advise the parent or carer accordingly and document the discussion.",
    },
    {
      checkId: "tbe-caut-autoimmune-cerebral",
      question: "Does the patient have an autoimmune disease or a pre-existing cerebral disorder?",
      cautionMessage:
        "Autoimmune disease and pre-existing cerebral disorders are cautions for TBE vaccination. An individual risk-benefit assessment should be documented before proceeding.",
    },
    {
      checkId: "tbe-caut-immunosuppressed",
      question: "Is the patient immunosuppressed due to disease or treatment?",
      cautionMessage:
        "Immunosuppression may reduce the immune response to TBE vaccine. Vaccination should still proceed as the vaccine is inactivated and safe. Consider arranging serology 4 weeks after the second dose to confirm an adequate antibody response. Counsel that protection may be suboptimal.",
    },
    {
      checkId: "tbe-caut-flavivirus-serology",
      question: "Is the patient likely to undergo serological testing for flavivirus infections (e.g. dengue, West Nile, Zika) in the near future?",
      cautionMessage:
        "TBE vaccination may cause false-positive results in flavivirus serology due to cross-reactivity. If serological testing for other flavivirus infections is planned, consider the timing of vaccination and inform the testing laboratory that the patient has received TBE vaccine.",
    },
    {
      checkId: "tbe-caut-pregnancy",
      question: "Is the patient pregnant or breastfeeding?",
      cautionMessage:
        "TBE vaccination in pregnancy should only proceed where protection is considered urgent and the risk of TBE infection outweighs any theoretical risk from the vaccine. Refer to the patient's GP or obstetrician before proceeding. Breastfeeding is not a contraindication.",
    },
    {
      checkId: "tbe-caut-syncope",
      question: "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for the duration of the observation period after administration.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against tick-borne encephalitis (TBE) only. It does not protect against Lyme disease or any other tick-borne infection.",
    "Continue to use tick-bite avoidance measures throughout your trip: wear long-sleeved clothing and long trousers tucked into socks in woodland and grassy areas, use an insect repellent containing DEET on exposed skin, check your skin carefully for ticks after outdoor activities, and remove any attached tick promptly using fine-tipped tweezers.",
    "Avoid consuming unpasteurised dairy products (milk, cheese) in TBE-endemic areas, as TBE can occasionally be transmitted through raw dairy from infected animals.",
    "After the first two doses, protection is expected for the ongoing tick season. The third dose is required for protection lasting 3 or more years \u2014 please ensure you return for your third dose.",
    "A booster dose will be needed to maintain protection \u2014 your pharmacist or travel health provider will advise you on the timing based on your age.",
    "If you develop a fever, headache, or flu-like symptoms during or after travel in a TBE-risk area, followed by any neurological symptoms (confusion, stiff neck, or difficulty with movement or coordination), seek urgent medical attention and tell the clinician you have been in a TBE-risk area.",
  ],
});
